import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios"
import {
  ApiErrorResponse,
  ApiSuccessResponse,
  STATUS_CODE_TO_ERROR_CODE,
} from "@/lib/errors/types"
import { refreshApi } from "./auth"
import { useAuthStore } from "@/hooks/use-auth"
import { setAuthSessionCookie } from "@/lib/auth/session-cookies"

const API_URL = process.env.NEXT_PUBLIC_API_URL

/** Public auth routes must never carry a prior session's Bearer token. */
const PUBLIC_AUTH_PATHS = [
  "/auth/login",
  "/auth/register",
  "/auth/refresh",
  "/auth/forgot-password",
  "/auth/validate-reset-token",
  "/auth/reset-password",
  "/auth/resend-verification",
  "/auth/verify-email",
] as const

function isPublicAuthUrl(url: string | undefined): boolean {
  if (!url) return false
  return PUBLIC_AUTH_PATHS.some((path) => url.includes(path))
}

/**
 * Centralized Axios Client
 * Single gateway for all API calls
 */
class ApiClient {
  private client: AxiosInstance
  private authToken: string | null = null
  private isRefreshing = false
  private refreshSubscribers: ((token: string) => void)[] = []

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      headers: {
        "Content-Type": "application/json",
      },
      // Default 30s timeout for most requests. Large operations should
      // use specific timeout overrides.
      timeout: 30000,
    })

    this.setupInterceptors()
  }

  private onRefreshed(token: string): void {
    this.refreshSubscribers.forEach((cb) => cb(token))
    this.refreshSubscribers = []
  }

  private subscribeToRefresh(cb: (token: string) => void): void {
    this.refreshSubscribers.push(cb)
  }

  /**
   * Set authentication token for subsequent requests
   */
  setAuthToken(token: string | null): void {
    this.authToken = token
  }

  /**
   * Setup request and response interceptors
   */
  private setupInterceptors(): void {
    // Request interceptor: Add auth header
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Login/register/etc. must not reuse a previous user's Authorization header
        if (isPublicAuthUrl(config.url)) {
          if (config.headers.Authorization) {
            delete config.headers.Authorization
          }
        } else if (!config.headers.Authorization) {
          // Prefer explicitly set token, then fall back to zustand-persist storage
          let token = this.authToken
          if (!token && typeof window !== "undefined") {
            try {
              const stored = localStorage.getItem("nova_auth")
              if (stored) {
                const parsed = JSON.parse(stored)
                token =
                  parsed?.state?.tokens?.access_token ??
                  parsed?.tokens?.access_token ??
                  null
              }
            } catch {
              token = null
            }
          }
          if (token) config.headers.Authorization = `Bearer ${token}`
        }

        // Add language header for i18n
        if (typeof window !== "undefined") {
          const locale = window.location.pathname.split("/")[1]
          if (locale === "ar" || locale === "en") {
            config.headers["Accept-Language"] = locale
            config.headers["x-locale"] = locale
          }
        }

        return config
      },
      (error) => Promise.reject(error)
    )

    // Response interceptor: Handle errors and token refresh
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError<ApiErrorResponse>) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & {
          _retry?: boolean
        }

        // Surface API failures in the browser console so deployed-env issues
        // can be traced without a debugger (method, URL, status, upstream code).
        if (!error.response || (error.response.status ?? 0) >= 500) {
          console.error(
            `[API] ${(originalRequest.method ?? "GET").toUpperCase()} ${originalRequest.url} ` +
              `failed — status=${error.response?.status ?? "network"} ` +
              `code=${error.response?.data?.error?.code ?? "none"} ` +
              `msg=${error.response?.data?.error?.message ?? error.message}`
          )
        }

        // Handle 401 Unauthorized — skip for login & refresh (those errors must surface directly)
        const isAuthRoute =
          originalRequest.url?.includes("/auth/login") ||
          originalRequest.url?.includes("/auth/refresh")
        const responseCode = error.response?.data?.error?.code
        // Idle/absolute session expiry is terminal — do not attempt refresh.
        if (
          error.response?.status === 401 &&
          responseCode === "AUTH_SESSION_EXPIRED"
        ) {
          useAuthStore.getState().clearSession()
          const locale = window.location.pathname.startsWith("/ar")
            ? "ar"
            : "en"
          window.location.href = `/${locale}/`
          return new Promise(() => {})
        }
        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !isAuthRoute
        ) {
          originalRequest._retry = true

          if (this.isRefreshing) {
            // Wait for refresh to complete and retry
            return new Promise((resolve, reject) => {
              this.subscribeToRefresh((token) => {
                if (!token) {
                  return reject(error)
                }
                originalRequest.headers.Authorization = `Bearer ${token}`
                resolve(this.client.request(originalRequest))
              })
            })
          }

          this.isRefreshing = true

          try {
            // Get refresh token from localStorage (zustand-persist wraps state under .state key)
            const authData = localStorage.getItem("nova_auth")
            if (!authData) throw new Error("No auth data")

            const parsed = JSON.parse(authData)
            const tokens = parsed?.state?.tokens ?? parsed?.tokens
            if (!tokens?.refresh_token) throw new Error("No refresh token")

            // Call refresh API — this goes through the same interceptor but
            // /auth/refresh is excluded from the 401 handler above, so a 401
            // response will fall through to standardizeError and be thrown
            // as a normal rejected promise caught by the catch block below.
            const res = await refreshApi(tokens.refresh_token)
            const newToken = res.data.access_token

            // Update stored tokens — preserve zustand-persist shape
            const existing = JSON.parse(authData)
            const existingUser = existing?.state?.user ?? existing?.user
            localStorage.setItem(
              "nova_auth",
              JSON.stringify({
                ...existing,
                state: { user: existingUser, tokens: res.data },
              })
            )

            const existingRole =
              existingUser?.role ??
              useAuthStore.getState().user?.role
            await setAuthSessionCookie({
              accessToken: newToken,
              maxAge: res.data.expires_in,
              role: existingRole,
            })

            // Update auth token for subsequent requests
            this.setAuthToken(newToken)
            this.onRefreshed(newToken)
            this.isRefreshing = false

            // Retry original request
            originalRequest.headers.Authorization = `Bearer ${newToken}`
            return this.client.request(originalRequest)
          } catch (refreshError) {
            this.isRefreshing = false
            // Notify waiting subscribers that refresh failed
            this.refreshSubscribers = []

            // The refresh failed — session is dead. Force logout.
            // The error may be an AxiosError (raw) or an ApiErrorResponse
            // (already standardized by our interceptor). Handle both.
            const apiErr = refreshError as ApiErrorResponse | undefined
            const axiosErr = refreshError as
              | AxiosError<ApiErrorResponse>
              | undefined
            const status =
              axiosErr?.response?.status ?? apiErr?.error?.statusCode
            const apiCode =
              axiosErr?.response?.data?.error?.code ?? apiErr?.error?.code

            // Any refresh failure means the session is unrecoverable — always logout
            if (
              status === 401 ||
              apiCode === "AUTH_TOKEN_EXPIRED" ||
              apiCode === "AUTH_SESSION_EXPIRED" ||
              apiCode === "AUTH_UNAUTHORIZED" ||
              !status // No status means no auth data / parse error — also dead
            ) {
              useAuthStore.getState().clearSession()
              const locale = window.location.pathname.startsWith("/ar")
                ? "ar"
                : "en"
              window.location.href = `/${locale}/`
              return new Promise(() => {}) // Prevent further error handling during redirect
            }
            return Promise.reject(refreshError)
          }
        }

        // Transform axios errors to standardized format
        const standardizedError = this.standardizeError(error)
        return Promise.reject(standardizedError)
      }
    )
  }

  /**
   * Standardize error to match our API contract
   */
  private standardizeError(
    error: AxiosError<ApiErrorResponse>
  ): ApiErrorResponse {
    // If backend returned our standardized format
    if (error.response?.data?.success === false) {
      return error.response.data
    }

    // Handle network errors
    if (!error.response) {
      return {
        success: false,
        error: {
          code: "SERVICE_UNAVAILABLE",
          message: "Network error. Please check your connection.",
          statusCode: 0,
        },
        timestamp: new Date().toISOString(),
        path: error.config?.url ?? "unknown",
      }
    }

    // Fallback: Convert HTTP status to error code
    const statusCode = error.response.status
    const errorCode = STATUS_CODE_TO_ERROR_CODE[statusCode] ?? "INTERNAL_ERROR"
    const message =
      typeof error.response.data === "string"
        ? error.response.data
        : ((error.response.data as { message?: string })?.message ??
          error.message ??
          "An error occurred")

    return {
      success: false,
      error: {
        code: errorCode,
        message,
        statusCode,
      },
      timestamp: new Date().toISOString(),
      path: error.config?.url ?? "unknown",
    }
  }

  /**
   * Generic request method with typed response
   */
  async request<T>(config: AxiosRequestConfig): Promise<ApiSuccessResponse<T>> {
    const response = await this.client.request<ApiSuccessResponse<T>>(config)
    return response.data
  }

  /**
   * HTTP methods
   */
  async get<T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiSuccessResponse<T>> {
    return this.request<T>({ ...config, method: "GET", url })
  }

  async post<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<ApiSuccessResponse<T>> {
    return this.request<T>({ ...config, method: "POST", url, data })
  }

  async put<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<ApiSuccessResponse<T>> {
    return this.request<T>({ ...config, method: "PUT", url, data })
  }

  async patch<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<ApiSuccessResponse<T>> {
    return this.request<T>({ ...config, method: "PATCH", url, data })
  }

  async delete<T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiSuccessResponse<T>> {
    return this.request<T>({ ...config, method: "DELETE", url })
  }
}

// Export singleton instance
export const apiClient = new ApiClient()
